'use client'

import * as React from 'react'
import { StoryCardHero, type StoryCardHeroProps } from './story-card-hero'

// StoryCardHeroRotator — a nav-pill row that swaps between several
// StoryCardHero configs. Same visual grammar as a single StoryCardHero
// (source -> result -> identified -> headline -> value chips), just with
// a tab strip above it so one page can prove several capabilities without
// stacking N full hero cards down the scroll.
//
// Reuses <StoryCardHero> directly rather than re-implementing its layout,
// keyed by the active tab so the reveal animation replays on every switch.

export type StoryCardHeroRotatorState = StoryCardHeroProps & {
  key: string
  navLabel: string
}

export function StoryCardHeroRotator({
  states,
  className = '',
}: {
  states: StoryCardHeroRotatorState[]
  className?: string
}) {
  const [active, setActive] = React.useState(states[0]?.key)
  const activeState = states.find((s) => s.key === active) ?? states[0]
  if (!activeState) return null

  return (
    <div className={className}>
      <div role="tablist" aria-label="Choose a capability to preview" className="mb-5 flex flex-wrap justify-center gap-2">
        {states.map((s) => {
          const selected = s.key === activeState.key
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(s.key)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                selected
                  ? 'border-foreground bg-foreground text-background'
                  : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
              }`}
            >
              {s.navLabel}
            </button>
          )
        })}
      </div>

      <div className="mx-auto max-w-md">
        {(() => {
          const { key: stateKey, ...heroProps } = activeState
          return <StoryCardHero key={stateKey} {...heroProps} />
        })()}
      </div>
    </div>
  )
}
